import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const ITConsultingCompaniesDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'technology-strategy',
      title: 'Technology Strategy & Roadmapping',
      description: 'Align your technology investments with business objectives through comprehensive strategy development. We assess your current IT landscape, identify gaps, and create actionable roadmaps that drive measurable business outcomes.',
      icon: ICONS.roadmap,
      features: [
        'IT Maturity Assessment & Benchmarking',
        'Digital Transformation Roadmaps',
        'Technology Investment Planning',
        'Risk Assessment & Mitigation Strategies',
        'Stakeholder Alignment Workshops',
        'Quarterly Strategy Review & Adjustment'
      ]
    },
    {
      id: 'cloud-architecture',
      title: 'Cloud Architecture Consulting',
      description: 'Design scalable, secure, and cost-effective cloud architectures tailored to your business needs. We help you choose the right cloud platform and services to maximize performance while minimizing operational overhead.',
      icon: ICONS.architecture,
      features: [
        'Cloud Readiness Assessment',
        'Multi-cloud & Hybrid Architecture Design',
        'Security & Compliance Architecture',
        'Cost Optimization & FinOps Strategy',
        'Disaster Recovery & Business Continuity',
        'Cloud-Native Application Design'
      ]
    },
    {
      id: 'process-optimization',
      title: 'Process Optimization & Automation',
      description: 'Streamline your business processes and eliminate inefficiencies through intelligent automation. We analyze workflows, identify bottlenecks, and implement automation solutions that reduce costs and improve productivity.',
      icon: ICONS.process,
      features: [
        'Business Process Analysis & Mapping',
        'Workflow Automation Implementation',
        'RPA (Robotic Process Automation)',
        'KPI Definition & Performance Tracking',
        'Change Management Support',
        'Continuous Improvement Frameworks'
      ]
    },
    {
      id: 'vendor-selection',
      title: 'Vendor Selection & Management',
      description: 'Make informed technology purchasing decisions with our vendor-neutral advisory services. We evaluate vendors, negotiate contracts, and manage relationships to ensure you get the best value from your technology partnerships.',
      icon: ICONS.partnership,
      features: [
        'Vendor Evaluation & Scoring',
        'RFP/RFI Development & Management',
        'Contract Negotiation Support',
        'SLA Definition & Monitoring',
        'Vendor Performance Reviews',
        'Technology Stack Rationalization'
      ]
    }
  ]

  const tools = [
    { name: 'TOGAF', category: 'Framework' },
    { name: 'ITIL', category: 'Service Mgmt' },
    { name: 'Agile', category: 'Methodology' },
    { name: 'Scrum', category: 'Methodology' },
    { name: 'AWS Well-Architected', category: 'Cloud' },
    { name: 'Azure Framework', category: 'Cloud' },
    { name: 'COBIT', category: 'Governance' },
    { name: 'Six Sigma', category: 'Process' },
    { name: 'Power BI', category: 'Analytics' },
    { name: 'Tableau', category: 'Analytics' },
    { name: 'SAP', category: 'Enterprise' },
    { name: 'Salesforce', category: 'CRM' }
  ]

  const benefits = [
    {
      title: 'Consulting Engagements',
      description: 'Successful technology consulting projects delivered across the UAE and the wider region.',
      stat: '150+'
    },
    {
      title: 'Average Cost Savings',
      description: 'Reduction in IT operational costs achieved through strategic optimization and automation.',
      stat: '35%'
    },
    {
      title: 'Industries Served',
      description: 'Diverse industries including finance, healthcare, retail, logistics, and government.',
      stat: '12+'
    },
    {
      title: 'Client Retention',
      description: 'Of our clients continue to engage us for ongoing technology consulting and support.',
      stat: '98%'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'What does an IT consulting company in Dubai do?',
      answer: 'An IT consulting company in Dubai helps businesses align their technology investments with strategic objectives. This includes developing technology roadmaps, designing cloud architectures, optimizing business processes through automation, evaluating and selecting vendors, conducting IT maturity assessments, and providing ongoing strategic advisory. We act as a trusted technology partner who bridges the gap between business goals and technical implementation.'
    },
    {
      question: 'How much does IT consulting cost in Dubai?',
      answer: 'IT consulting rates in Dubai range from AED 500 to AED 2,000 per hour depending on the seniority of the consultant and complexity of the engagement. Strategy and roadmap projects typically range from AED 30,000 to AED 100,000. Comprehensive digital transformation consulting engagements range from AED 75,000 to AED 350,000. We also offer monthly advisory retainers starting from AED 10,000 per month for ongoing strategic guidance.'
    },
    {
      question: 'How do you differ from other IT consulting firms in Dubai?',
      answer: 'We differentiate through our hands-on, implementation-focused approach. While many consulting firms deliver reports and recommendations, we work alongside your team to implement solutions. We are vendor-neutral, recommending technologies based on your needs rather than partnerships. Our consultants hold certifications in AWS, Azure, TOGAF, ITIL, and Agile methodologies, and we have deep experience with businesses operating in UAE free zones and regulatory environments.'
    },
    {
      question: 'How long does a typical IT consulting engagement last?',
      answer: 'Engagement length varies by scope. A focused technology assessment or vendor evaluation takes 2 to 4 weeks. A technology strategy and roadmap project typically takes 4 to 8 weeks. Comprehensive digital transformation consulting can span 3 to 12 months depending on the organization size and complexity. Many clients start with a short assessment and then engage us for ongoing advisory support on a monthly retainer basis.'
    },
    {
      question: 'Can you help with cloud architecture and migration consulting?',
      answer: 'Yes, cloud architecture consulting is one of our core services. We help Dubai businesses design scalable, secure, and cost-effective cloud architectures on AWS, Azure, or GCP. This includes cloud readiness assessments, multi-cloud and hybrid architecture design, security and compliance architecture, FinOps and cost optimization strategy, disaster recovery planning, and cloud-native application design. We follow AWS Well-Architected and Azure Framework best practices.'
    },
    {
      question: 'What frameworks and methodologies do you use?',
      answer: 'We use industry-recognized frameworks including TOGAF for enterprise architecture, ITIL for IT service management, Agile and Scrum for project delivery, COBIT for IT governance, Six Sigma for process optimization, and the AWS Well-Architected Framework and Azure Framework for cloud architecture. We select methodologies based on your organization maturity and project requirements rather than applying a one-size-fits-all approach.'
    },
    {
      question: 'Do you help with vendor selection and contract negotiation?',
      answer: 'Yes, our vendor-neutral advisory services include developing RFP and RFI documents, evaluating and scoring vendor proposals, negotiating contracts and SLAs, conducting proof-of-concept evaluations, managing vendor relationships, and performing technology stack rationalization to eliminate redundant tools. We help you make informed purchasing decisions and get the best value from your technology partnerships.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'IT Consulting Dubai', path: '/it-consulting-companies-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="IT Consulting Companies in Dubai | Expert Technology Consulting UAE"
        description="Leading IT consulting company in Dubai. We provide technology strategy, cloud architecture consulting, process optimization, and vendor management services. Transform your business with Demirix."
        keywords={[
          'it consulting companies in dubai',
          'it consulting dubai',
          'it consultant dubai',
          'technology consulting dubai',
          'it consulting firms uae',
          'technology advisory dubai',
          'it strategy consulting dubai',
          'cloud consulting dubai',
          'digital transformation consulting uae',
          'business technology consulting dubai',
          'it management consulting uae',
          'enterprise it consulting dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              IT Consulting
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              IT Consulting Companies in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Strategic technology consulting helping Dubai businesses optimize operations,
              reduce costs, and drive innovation. We bridge the gap between business goals
              and technology solutions to deliver measurable results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule IT Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our IT Consulting Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Comprehensive technology consulting solutions designed for Dubai businesses looking to
                optimize their IT operations and drive digital transformation.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frameworks & Tools We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage industry-leading frameworks and tools to deliver strategic, results-driven consulting.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about IT consulting services in Dubai answered by our team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Dubai Services
              </Link>
              <Link
                to="/software-development-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Software Development
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Optimize Your Technology Strategy?"
        description="Let's discuss how our IT consulting expertise can help your Dubai business reduce costs, improve efficiency, and accelerate innovation."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default ITConsultingCompaniesDubai
